import { Box } from "@mui/material";
import { SideNav } from "components";
import Card from "components/CardSelector/Card";
import { CardSelectorWrapper } from "components/CardSelector/styles";
import { HomeWrapper } from "components/CdBalance/style";
import { LargeButton, PageTitle, Ptag } from "components/core";
import routes from "components/routes";
import { WidthLimiter } from "pages/PlanSetup/styles";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import http from "services/httpService";
import styled from "styled-components";

const options = ["Conventional", "Flex"];
const data_collection_option = ["Yes", "No"];

// Custom hook for plan selection persistence
const usePlanPersistence = (storageKey) => {
  const timeoutRef = useRef(null);
  const isClearing = useRef(false);
  
  // Load saved selections
  const loadSavedSelections = useCallback(() => {
    try {
      const savedData = localStorage.getItem(storageKey);
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      console.warn('Failed to load saved plan selections:', error);
      return null;
    }
  }, [storageKey]);

  // Save selections with debounce
  const saveSelections = useCallback((selections) => {
    // Don't save if we're in the process of clearing
    if (isClearing.current) {
      return;
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      // Double check we're not clearing
      if (isClearing.current) {
        return;
      }
      
      if (selections && (selections.plan || selections.dataCollection || selections.depAddRemove || selections.depEdit)) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(selections));
        } catch (error) {
          console.warn('Failed to save plan selections to localStorage:', error);
        }
      }
    }, 300); // Save after 300ms of inactivity
  }, [storageKey]);

  // Clear saved selections
  const clearSavedSelections = useCallback(() => {
    isClearing.current = true;
    
    // Clear any pending save operations
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear saved plan selections:', error);
    } finally {
      // Reset the clearing flag after a short delay to allow for navigation
      setTimeout(() => {
        isClearing.current = false;
      }, 1000);
    }
  }, [storageKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { loadSavedSelections, saveSelections, clearSavedSelections };
};

function PlanSelection() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState("");
  const [dataCollection, setDataCollection] = useState();
  const [depAddRemove, setDepAddRemove] = useState();
  const [depEdit, setDepEdit] = useState();
  const [disabled, setDisabled] = useState(true);
  const [showAutoSaveIndicator, setShowAutoSaveIndicator] = useState(false);
  const [hasSavedSelections, setHasSavedSelections] = useState(false);

  // Storage key for persistence - using the same key for both draft and final data
  const storageKey = 'plan_selection_data';
  
  // Use the persistence hook
  const { loadSavedSelections, saveSelections, clearSavedSelections } = usePlanPersistence(storageKey);

  // Load saved selections on component mount
  useEffect(() => {
    const savedSelections = loadSavedSelections();
    if (savedSelections) {
      
      // Restore each field if it exists in saved data
      if (savedSelections.plan !== undefined) setPlan(savedSelections.plan);
      if (savedSelections.dataCollection !== undefined) setDataCollection(savedSelections.dataCollection);
      if (savedSelections.depAddRemove !== undefined) setDepAddRemove(savedSelections.depAddRemove);
      if (savedSelections.depEdit !== undefined) setDepEdit(savedSelections.depEdit);
    } else {
      console.log('No saved selections found');
    }
  }, []);

  // Save selections whenever they change
  useEffect(() => {
    const currentSelections = {
      plan,
      dataCollection,
      depAddRemove,
      depEdit
    };
    
    // Only save if at least one selection is made
    if (plan || dataCollection || depAddRemove || depEdit) {
      saveSelections(currentSelections);
    }
  }, [plan, dataCollection, depAddRemove, depEdit, saveSelections]);

  // Show auto-save indicator briefly when selections are auto-saved
  useEffect(() => {
    if (plan || dataCollection || depAddRemove || depEdit) {
      setShowAutoSaveIndicator(true);
      const timer = setTimeout(() => {
        setShowAutoSaveIndicator(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [plan, dataCollection, depAddRemove, depEdit]);

  const resetPlan = (name) => {
    setDataCollection(name);
    setDepAddRemove("");
    setDepEdit("");
  };
  const resetAddDelet = (name) => {
    setDepAddRemove(name);
    setDepEdit("");
  };
  useEffect(() => {
    // For Conventional: require plan and depEdit
    if (plan === "Conventional") {
      if (!depEdit) {
        setDisabled(true);
        return;
      }
      setDisabled(false);
      return;
    }

    // For Flex (existing flow)
    if (!plan || !dataCollection) {
      setDisabled(true);
    } else if (dataCollection === "Yes" && !depAddRemove) {
      setDisabled(true);
    } else if (dataCollection === "Yes" && depAddRemove && !depEdit) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [plan, dataCollection, depAddRemove, depEdit]);
  const handleSubmit = async () => {    
    const flex = plan === "Conventional" ? false : true;
    const employee_collection_required = flex ? (dataCollection === "Yes") : false;
    const add_dependents_permission = flex && dataCollection === "Yes" ? (depAddRemove === "Yes") : false;
    const edit_dependents_permission = depEdit === "Yes" ? true : false;  
    const company = { flex, employee_collection_required };
    company.add_dependents_permission = add_dependents_permission;
    company.edit_dependents_permission = edit_dependents_permission; 
    try {
      await http.post("/business/edit_company/", {
        company,
      });
      
      
      // Save other related data
      localStorage.setItem("global_plan_type", plan);
      localStorage.setItem("universal_product", flex);
      
    
      navigate(`/${routes.UNIVERSAL_BENEFITS_SETUP}`);
    } catch (e) {
      console.log('API call failed:', e);
      alert(e.response.data);
    }
  };

  // Clear data when user explicitly wants to start fresh  
  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear all saved plan selections?')) {
      clearSavedSelections();
      setPlan("");
      setDataCollection();
      setDepAddRemove();
      setDepEdit();
      setHasSavedSelections(false);
    }
  };

  return (
    <>
      <SideNav />
      <HomeWrapper style={{ minHeight: "100vh", }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <PageTitle title="Company Information" />
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          
          </div>
        </div>
        
        <WidthLimiter>
          <SubHeader mt={'24px'} >Choose Benefit Type</SubHeader>
          <CardSelectorWrapper>
            {options.map((display_name, index) => (
              <Card
                key={index}
                title={display_name}
                selected={plan === display_name}
                onChange={() => setPlan(display_name)}
              />
            ))}
          </CardSelectorWrapper>
          {plan === "Conventional" ? (
            <>
              <SubHeader>Allow Edit of Dependents</SubHeader>
              <CardSelectorWrapper>
                {data_collection_option.map((display_name, index) => (
                  <Card
                    key={index}
                    title={display_name}
                    selected={depEdit === display_name}
                    onChange={() => setDepEdit(display_name)}
                  />
                ))}
              </CardSelectorWrapper>
            </>
          ) : (
            <>
              <SubHeader>Allow Data Collection from Employees</SubHeader>
              <CardSelectorWrapper>
                {data_collection_option.map((display_name, index) => (
                  <Card
                    key={index}
                    title={display_name}
                    selected={dataCollection === display_name}
                    onChange={() => resetPlan(display_name)}
                  />
                ))}
              </CardSelectorWrapper>
              {dataCollection === "Yes" && (
                <>
                  <SubHeader>Allow Addition/Removal of Dependents</SubHeader>
                  <CardSelectorWrapper>
                    {data_collection_option.map((display_name, index) => (
                      <Card
                        key={index}
                        title={display_name}
                        selected={depAddRemove === display_name}
                        onChange={() => resetAddDelet(display_name)}
                      />
                    ))}
                  </CardSelectorWrapper>
                  {depAddRemove && (
                    <div style={{ paddingBottom: "5rem" }}>
                      <SubHeader>Allow Edit of Dependents</SubHeader>
                      <CardSelectorWrapper>
                        {data_collection_option.map((display_name, index) => (
                          <Card
                            key={index}
                            title={display_name}
                            selected={depEdit === display_name}
                            onChange={() => setDepEdit(display_name)}
                          />
                        ))}
                      </CardSelectorWrapper>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </WidthLimiter>
        <ButtonWrapperNew>
          <Box sx={{marginBottom:'60px'}}>
            <LargeButton
              id=""
              label="Save & Next"
              disabled={disabled}
              type="submit"
              opacity={disabled ? "0.5" : "1"}
              onClick={handleSubmit}
            />
          </Box>
        </ButtonWrapperNew>
      </HomeWrapper>
    </>
  );
}

export default PlanSelection;

export const ButtonWrapper = styled.div`
  position: absolute;
  width: 800px;
  bottom: 6rem;
`;

export const ButtonWrapperNew = styled.div`
  position: absolute;
  width: 932px;
  margin-top: 2rem;
  bottom: 1rem;
`;

export const SubHeader = ({ children ,mt}) => {
  return (
    <Ptag marginTop={mt?mt:"36px"} fontSize="16px" fontWeight="600" marginBottom="12px">
      {children}
    </Ptag>
  );
};
